import type { BasicData, GasProperties } from "../../fluidProperties/d";
import type { GasPercentages } from "../../productionRates";
import { Ratio } from "@/libs/units/percentage";
import { calculateGasProperties } from "./calculateGasProperties";
import type { GasEntry } from "./calculateGasProperties";
import { gasPropertiesByKey } from "./gasPropertiesTable";

export const getGasPropertiesForComponent = (key: string): GasProperties => {
  const entryGasProperties = gasPropertiesByKey.get(key);
  if (!entryGasProperties) throw new Error(`INVALID GAS PROPERTIES: ${key}`);
  return entryGasProperties;
};

// One component's share of the gas, for its percentage.
export const getGasContribution = (
  entryGasProperties: GasProperties,
  percent: number
): GasEntry => {
  const ratio = Ratio.toThisUnit(percent);
  return {
    gasPercentage: ratio,
    yM: ratio * entryGasProperties.molarMass,
    yTc: ratio * entryGasProperties.criticalTemp,
    yPc: ratio * entryGasProperties.criticalPressure
  };
};

// Calculate the gas entries based on the dynamic percentages
export function getGasContributions(
  gasPercentages: GasPercentages
): GasEntry[] {
  const retval: GasEntry[] = [];

  for (const [key, value] of Object.entries(gasPercentages)) {
    retval.push(getGasContribution(getGasPropertiesForComponent(key), value));
  }

  return retval;
}

export const getGasProperties = (
  basicData: BasicData,
  temperatureF: number,
  pressurePSIA: number,
  gasFlowRateSCFD: number
): GasProperties[] => {
  const { gasPercentages, bottomTubingPressurePSIA } = basicData;
  const effectiveTubingPressurePSIA =
    bottomTubingPressurePSIA > 0 ? bottomTubingPressurePSIA : pressurePSIA;

  // Calculate the gas entries based on the dynamic percentages
  const gasProperties: GasProperties[] = calculateGasProperties(
    gasPercentages,
    temperatureF,
    pressurePSIA,
    gasFlowRateSCFD,
    effectiveTubingPressurePSIA
  );
  // Return the calculated properties along with the original gas properties
  return gasProperties;
};
